import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../components/api";
import CuruChatsPost from "../components/CuruChatsPost";
import PostModel from "../components/PostModel";
import BannerIcon from "../images/curu-chats-header.svg";
import { FollowProvider } from "../context/FollowContext.jsx";
import "./CuruChatsPage.css";
import Icons from "../components/Icons.jsx";

export default function CuruChatsPage() {
  const [profile, setProfile] = useState(null);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [visibleCountTrending, setVisibleCountTrending] = useState(2); // for counting visible trending posts
  const [visibleCountRecent, setVisibleCountRecent] = useState(2); // for counting visible recent posts
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const res = await api.get("/comunity/category");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch posts from API
  const fetchPosts = async () => {
    setIsLoading(true); // Start loading
    try {
      const res = await api.get("/comunity/post");
      setPosts(res.data || []);
      //console.log(res.data); // Un-comment if you want to see the post items in the console
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false); // Stop loading no matter what
    }
  };

  const desiredOrder = [
    "Ask the community",
    "Show and tell",
    "Tips for use",
    "Trends",
    "Beauty steals",
    "My Routine",
    "Recommended",
  ];

  const orderedCategories = [...categories].sort(
    (a, b) => desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name)
  );

  const goToCategoryPage = (category) => {
    navigate(`/curu-chats/category/${category.id}`, {
      state: { category: category },
    });
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/"); // redirect immediately if no token
        return;
      }

      try {
        const response = await fetch(
          "https://dev-api.curu.com.au/api/profile",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          console.error("Failed to fetch profile:", response.statusText);
          return;
        }

        const data = await response.json();
        setProfile(data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };

    fetchProfile();
    fetchCategories();
    fetchPosts();
  }, [navigate]);

  const handleMakePostClick = () => {
    setIsModelOpen(true);
  };

  const handleProfileClick = (user) => {
    if (!user || !user.id) return;
    navigate(`/profile/${user.id}`, { state: { user } });
  };

  return (
    <>
      <div>
        <FollowProvider>
          <div className="header-container">
            <p className="header">Curu Chats</p>
          </div>
          <div className="banner-icon">
            <img
              src={BannerIcon}
              alt="Curu Chats Banner"
              className="banner-image"
            />
            <div className="banner-text-container">
              <p className="banner-text">
                Your beauty community #LetsTalkBeauty
              </p>
            </div>
          </div>
          <p className="new-post-heading">New Post</p>
          <div className="new-post-container">
            {profile?.photo ? (
              <img
                src={profile.photo}
                alt={"User Icon"}
                className="user-icon"
              />
            ) : (
              <div className="user-icon">
                <Icons.Profile />
              </div>
            )}
            <button className="new-post-button" onClick={handleMakePostClick}>
              Start a post, share your views about a product, etc
            </button>
          </div>
          <p className="sub-heading">Categories</p>
          <div className="chats-categories-container">
            {orderedCategories.map((cat, idx) => (
              <div
                className="chats-category-container"
                key={idx}
                onClick={() => goToCategoryPage(cat)}
              >
                <img
                  className="chats-category-image"
                  src={cat.image || "/default-icon.png"}
                  alt={cat.name}
                />
                <button className="chats-category-button">{cat.name}</button>
              </div>
            ))}
          </div>

          <p className="sub-heading">Trending</p>
          <div className="posts-container">
            {isLoading ? (
              <p>Loading posts...</p>
            ) : posts && posts.length > 0 ? (
              <>
                {posts
                  .sort((a, b) => (b.like_count || 0) - (a.like_count || 0)) // Sort by like_count
                  .slice(0, visibleCountTrending)
                  .map((post) => (
                    <CuruChatsPost
                      key={post.id}
                      post={post}
                      onProfileClick={handleProfileClick}
                      onDeleteSuccess={() =>
                        setPosts((prev) => prev.filter((p) => p.id !== post.id))
                      }
                    />
                  ))}
                {visibleCountTrending < posts.length && (
                  <div className="text-center my-4">
                    <button
                      className="submit-button"
                      onClick={() =>
                        setVisibleCountTrending((prev) => prev + 2)
                      }
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No posts found.</p>
            )}
          </div>

          <p className="sub-heading">Recent</p>
          <div className="posts-container">
            {isLoading ? (
              <p>Loading posts...</p>
            ) : posts && posts.length > 0 ? (
              <>
                {posts
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  ) // Sort by created_at
                  .slice(0, visibleCountRecent)
                  .map((post) => (
                    <CuruChatsPost
                      key={post.id}
                      post={post}
                      onProfileClick={handleProfileClick}
                      onDeleteSuccess={() =>
                        setPosts((prev) => prev.filter((p) => p.id !== post.id))
                      }
                    />
                  ))}
                {visibleCountRecent < posts.length && (
                  <div className="text-center my-4">
                    <button
                      className="submit-button"
                      onClick={() => setVisibleCountRecent((prev) => prev + 2)}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No posts found.</p>
            )}
          </div>
        </FollowProvider>
      </div>

      {isModelOpen && (
        <PostModel
          profile={profile}
          isOpen={isModelOpen}
          onClose={() => setIsModelOpen(false)}
          onSubmit={() => {
            setIsModelOpen(false);
            fetchPosts(); // Re-fetch posts after submission if needed
          }}
        />
      )}
    </>
  );
}
